import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ActionWithPayload } from "@obg/ngrx-utils";
import {
	AppError,
	AUTH_ACTION_TYPE,
	MessageScopeType,
	MessageService,
	MessageType,
	SnackBarCacheService,
} from "@obg/ngx.core";
import { merge, of } from "rxjs";
import { catchError, filter, map, switchMap, tap } from "rxjs/operators";

import { NetlossLimitPromptService } from "../netloss-limit-prompt/netloss-limit-prompt.service";

import { BETTING_LIMIT_ACTION_TYPE, BettingLimitAction } from "./betting-limit.action";
import { BettingLimitConfigService } from "./betting-limit.config";
import { BETTING_LIMIT_MESSAGE_CONTEXT } from "./betting-limit.const";
import {
	BettingLimit,
	BettingLimitMessageCodes,
	BettingLimitParams,
	BettingLimitStatus,
	BettingLimitType,
	TimedBettingLimitParams,
	WageringLimitParams,
} from "./betting-limit.model";
import { BettingLimitService } from "./betting-limit.service";
import { NETLOSS_LIMIT_80_ID } from "./netloss-limit/netloss-limit.const";
import {
	getNetlossLimitConfirmation,
	getNetlossLimitConfirmationFail,
	getNetlossLimitConfirmationSuccess,
	netlossConfirmationDialogDisplayed,
	updateNetLossLimitConfirmation,
	updateNetLossLimitConfirmationFail,
	updateNetLossLimitConfirmationSuccess,
} from "./netloss-limit-confirmation/netloss-limit-confirmation.action";

@Injectable()
export class BettingLimitEffect {
	betCountLimit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.getBetCountLimit),
			switchMap(() =>
				this.service.getBetCountLimits().pipe(
					map((response) => BettingLimitAction.getBetCountLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.getBetCountLimitFail(error)))
				)
			)
		)
	);

	betSizeLimit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.getBetSizeLimit),
			switchMap(() =>
				this.service.getBetSizeLimits().pipe(
					map((response) => BettingLimitAction.getBetSizeLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.getBetSizeLimitFail(error)))
				)
			)
		)
	);

	wageringLimit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.getWageringLimit),
			switchMap(() =>
				this.service.getWageringLimits().pipe(
					map((response) => BettingLimitAction.getWageringLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.getWageringLimitFail(error)))
				)
			)
		)
	);

	timedWageringLimit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.getTimedWageringLimit),
			switchMap(() =>
				this.service.getTimedWageringLimits().pipe(
					map((response) => BettingLimitAction.getTimedWageringLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.getTimedWageringLimitFail(error)))
				)
			)
		)
	);
	remainingBetLimit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.getRemainingBetLimit),
			switchMap(() =>
				this.service.getRemainingBetLimit().pipe(
					map((response) => BettingLimitAction.getRemainingBetLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.getRemainingBetLimitFail(error)))
				)
			)
		)
	);

	netlossLimit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.getNetlossLimit),
			switchMap(() =>
				this.service.getNetlossLimits().pipe(
					map((response) => BettingLimitAction.getNetlossLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.getNetlossLimitFail(error)))
				)
			)
		)
	);

	getNetlossLimitConfirmation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getNetlossLimitConfirmation),
			switchMap(() =>
				this.service.getNetLossLimitConfirmation().pipe(
					filter((payload) => !!payload),
					map((payload) => getNetlossLimitConfirmationSuccess({ payload })),
					catchError((error: AppError) => of(getNetlossLimitConfirmationFail({ error })))
				)
			)
		)
	);

	netlossConfirmationDialogDisplayed$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(getNetlossLimitConfirmationSuccess),
				switchMap(() => this.netlossLimitPromptService.initialize$()),
				map(netlossConfirmationDialogDisplayed)
			),
		{ dispatch: false }
	);

	netlossLimitConfirmationUpdated$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateNetLossLimitConfirmation),
			switchMap(({ payload }) =>
				this.service.updateNetLossLimitConfirmation({ status: payload.status }).pipe(
					map(() => updateNetLossLimitConfirmationSuccess({ payload })),
					catchError((error: AppError) => of(updateNetLossLimitConfirmationFail({ error })))
				)
			)
		)
	);

	netlossLimitMessage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(BETTING_LIMIT_ACTION_TYPE.getNetlossLimitSuccess),
				map((x: ActionWithPayload<BettingLimit[]>) => x.payload),
				filter(() => this.bettingLimitConfigService.getResetNetlossDoNotShowAgainBtnState()),
				tap((limits) => {
					const activeOrQueuedLimits = limits.filter(
						(limit) =>
							limit.status === BettingLimitStatus.Active || limit.status === BettingLimitStatus.Queued
					);
					const newestLimit = activeOrQueuedLimits.reduce((acc, curr) =>
						acc.createdDate < curr.createdDate ? curr : acc
					);
					if (newestLimit.status === BettingLimitStatus.Active) {
						this.snackBarCacheService.removeSnackbarDoNotShowAgain(NETLOSS_LIMIT_80_ID);
					} else if (newestLimit.status === BettingLimitStatus.Queued) {
						const now = Date.now();
						const startDate = new Date(newestLimit.startDate).getTime();
						this.snackBarCacheService.changeSnackbarDoNotShowAgainExpiration(
							NETLOSS_LIMIT_80_ID,
							startDate - now
						);
					}
				})
			),
		{ dispatch: false }
	);

	betCountLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.updateBetCountLimit),
			map((x: ActionWithPayload<BettingLimitParams>) => x.payload),
			switchMap((params) =>
				this.service.updateBetCountLimit(params).pipe(
					map((response) => BettingLimitAction.updateBetCountLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.updateBetCountLimitFail(error)))
				)
			)
		)
	);

	betCountLimitDelete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeBetCountLimit),
			map((x: ActionWithPayload<BettingLimitType>) => x.payload),
			switchMap(() =>
				this.service.removeBetCountLimit().pipe(
					map((response) => BettingLimitAction.deleteBetCountLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.deleteBetCountLimitFail(error)))
				)
			)
		)
	);

	betSizeLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.updateBetSizeLimit),
			map(({ payload }: ActionWithPayload<BettingLimitParams>) => payload),
			switchMap((params) =>
				this.service.updateBetSizeLimit(params).pipe(
					map((response) => BettingLimitAction.updateBetSizeLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.updateBetSizeLimitFail(error)))
				)
			)
		)
	);

	betSizeLimitDelete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeBetSizeLimit),
			map(({ payload }: ActionWithPayload<BettingLimitType>) => payload),
			switchMap(() =>
				this.service.removeBetSizeLimit().pipe(
					map((response) => BettingLimitAction.deleteBetSizeLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.deleteBetSizeLimitFail(error)))
				)
			)
		)
	);

	wageringLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.updateWageringLimit),
			map((x: ActionWithPayload<WageringLimitParams>) => x.payload),
			switchMap((params) =>
				this.service.updateWageringLimit(params).pipe(
					map((response) => BettingLimitAction.updateWageringLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.updateWageringLimitFail(error)))
				)
			)
		)
	);

	wageringLimitDelete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeWageringLimit),
			map((x: ActionWithPayload<BettingLimitType>) => x.payload),
			switchMap(() =>
				this.service.removeWageringLimit().pipe(
					map((response) => BettingLimitAction.deleteWageringLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.deleteWageringLimitFail(error)))
				)
			)
		)
	);

	timedWageringLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.updateTimedWageringLimit),
			map((x: ActionWithPayload<TimedBettingLimitParams>) => x.payload),
			switchMap((params) =>
				this.service.updateTimedWageringLimit(params).pipe(
					map((response) => BettingLimitAction.updateTimedWageringLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.updateTimedWageringLimitFail(error)))
				)
			)
		)
	);

	timedWageringLimitDelete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeTimedWageringLimit),
			map((x: ActionWithPayload<BettingLimitType>) => x.payload),
			switchMap(() =>
				this.service.removeTimedWageringLimit().pipe(
					map((response) => BettingLimitAction.deleteTimedWageringLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.deleteTimedWageringLimitFail(error)))
				)
			)
		)
	);

	timedWageringLimitWithPayloadDelete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeTimedWageringLimitWithPayload),
			map((x: ActionWithPayload<TimedBettingLimitParams>) => x.payload),
			switchMap((params) =>
				this.service.removeTimedWageringLimitWithPayload(params).pipe(
					map((response) => BettingLimitAction.deleteTimedWageringLimitWithPayloadSuccess(response)),
					catchError((error: AppError) =>
						of(BettingLimitAction.deleteTimedWageringLimitWithPayloadFail(error))
					)
				)
			)
		)
	);

	netlossLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.updateNetlossLimit),
			map((x: ActionWithPayload<TimedBettingLimitParams>) => x.payload),
			switchMap((params) =>
				this.service.updateNetlossLimit(params).pipe(
					map((response) => BettingLimitAction.updateNetlossLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.updateNetlossLimitFail(error)))
				)
			)
		)
	);

	betCountLimitUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.updateBetCountLimitSuccess),
			map(() => BettingLimitAction.getBetCountLimit())
		)
	);

	betSizeLimitUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.updateBetSizeLimitSuccess),
			map(() => BettingLimitAction.getBetSizeLimit())
		)
	);

	wageringLimitUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.updateWageringLimitSuccess),
			map(() => BettingLimitAction.getWageringLimit())
		)
	);

	timedWageringLimitUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.updateTimedWageringLimitSuccess),
			map(() => BettingLimitAction.getTimedWageringLimit())
		)
	);

	netlossLimitUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.updateNetlossLimitSuccess),
			map(() => BettingLimitAction.getNetlossLimit())
		)
	);

	netlossLimitDeleteSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeNetlossLimitSuccess),
			map(() => BettingLimitAction.getNetlossLimit())
		)
	);

	netlossLimitWithPayloadDeleteSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeNetlossLimitWithPayloadSuccess),
			map(() => BettingLimitAction.getNetlossLimit())
		)
	);

	betCountLimitDeleteSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeBetCountLimitSuccess),
			map(() => BettingLimitAction.getBetCountLimit())
		)
	);

	betSizeLimitDeleteSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeBetSizeLimitSuccess),
			map(() => BettingLimitAction.getBetSizeLimit())
		)
	);

	wageringLimitDeleteSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeWageringLimitSuccess),
			map(() => BettingLimitAction.getWageringLimit())
		)
	);

	timedWageringLimitDeleteSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeTimedWageringLimitSuccess),
			map(() => BettingLimitAction.getTimedWageringLimit())
		)
	);

	timedWageringLimitWithPayloadDeleteSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeTimedWageringLimitWithPayloadSuccess),
			map(() => BettingLimitAction.getTimedWageringLimit())
		)
	);

	sessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => BettingLimitAction.reset())
		)
	);

	netlossLimitDelete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeNetlossLimit),
			switchMap(() =>
				this.service.removeNetlossLimit().pipe(
					map((response) => BettingLimitAction.deleteNetlossLimitSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.deleteNetlossLimitFail(error)))
				)
			)
		)
	);

	netlossLimitWithPayloadDelete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BETTING_LIMIT_ACTION_TYPE.removeNetlossLimitWithPayload),
			map((x: ActionWithPayload<TimedBettingLimitParams>) => x.payload),
			switchMap((params) =>
				this.service.removeNetlossLimitWithPayload(params).pipe(
					map((response) => BettingLimitAction.deleteNetlossLimitWithPayloadSuccess(response)),
					catchError((error: AppError) => of(BettingLimitAction.deleteNetlossLimitWithPayloadFail(error)))
				)
			)
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[BETTING_LIMIT_ACTION_TYPE.getBetCountLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.getBetCountLimit,
				[BETTING_LIMIT_ACTION_TYPE.getBetSizeLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.getBetSizeLimit,
				[BETTING_LIMIT_ACTION_TYPE.getWageringLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.getWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.getTimedWageringLimitFail]:
					BETTING_LIMIT_MESSAGE_CONTEXT.getTimedWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.getNetlossLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.getNetlossLimit,
				[BETTING_LIMIT_ACTION_TYPE.updateBetCountLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.updateBetCountLimit,
				[BETTING_LIMIT_ACTION_TYPE.updateWageringLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.updateWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.updateTimedWageringLimitFail]:
					BETTING_LIMIT_MESSAGE_CONTEXT.updateTimedWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.updateNetlossLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.updateNetlossLimit,
				[BETTING_LIMIT_ACTION_TYPE.removeBetCountLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.deleteBetCountLimit,
				[BETTING_LIMIT_ACTION_TYPE.removeBetSizeLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.deleteBetSizeLimit,
				[BETTING_LIMIT_ACTION_TYPE.removeWageringLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.deleteWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.removeTimedWageringLimitFail]:
					BETTING_LIMIT_MESSAGE_CONTEXT.deleteTimedWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.removeNetlossLimitFail]: BETTING_LIMIT_MESSAGE_CONTEXT.deleteNetlossLimit,
			}),
			this.messageService.mapClearByContext$({
				[BETTING_LIMIT_ACTION_TYPE.getBetCountLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.getBetCountLimit,
				[BETTING_LIMIT_ACTION_TYPE.getBetSizeLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.getBetSizeLimit,
				[BETTING_LIMIT_ACTION_TYPE.getWageringLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.getWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.getTimedWageringLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.getTimedWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.getNetlossLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.getNetlossLimit,
				[BETTING_LIMIT_ACTION_TYPE.updateBetCountLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.updateBetCountLimit,
				[BETTING_LIMIT_ACTION_TYPE.updateBetSizeLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.updateBetSizeLimit,
				[BETTING_LIMIT_ACTION_TYPE.updateWageringLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.updateWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.updateTimedWageringLimit]:
					BETTING_LIMIT_MESSAGE_CONTEXT.updateTimedWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.updateNetlossLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.updateNetlossLimit,
				[BETTING_LIMIT_ACTION_TYPE.removeBetCountLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.deleteBetCountLimit,
				[BETTING_LIMIT_ACTION_TYPE.removeBetSizeLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.deleteBetSizeLimit,
				[BETTING_LIMIT_ACTION_TYPE.removeWageringLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.deleteWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.removeTimedWageringLimit]:
					BETTING_LIMIT_MESSAGE_CONTEXT.deleteTimedWageringLimit,
				[BETTING_LIMIT_ACTION_TYPE.removeNetlossLimit]: BETTING_LIMIT_MESSAGE_CONTEXT.deleteNetlossLimit,
			}),
			this.messageService.map$({
				[BETTING_LIMIT_ACTION_TYPE.updateBetCountLimitSuccess]: {
					context: BETTING_LIMIT_MESSAGE_CONTEXT.updateBetCountLimit,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BettingLimitMessageCodes.UpdateBetCountLimitSuccess,
				},
				[BETTING_LIMIT_ACTION_TYPE.updateWageringLimitSuccess]: {
					context: BETTING_LIMIT_MESSAGE_CONTEXT.updateWageringLimit,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BettingLimitMessageCodes.UpdateWageringLimitSuccess,
				},
				[BETTING_LIMIT_ACTION_TYPE.updateTimedWageringLimitSuccess]: {
					context: BETTING_LIMIT_MESSAGE_CONTEXT.updateTimedWageringLimit,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BettingLimitMessageCodes.UpdateTimedWageringLimitSuccess,
				},
				[BETTING_LIMIT_ACTION_TYPE.updateNetlossLimitSuccess]: {
					context: BETTING_LIMIT_MESSAGE_CONTEXT.updateNetlossLimit,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BettingLimitMessageCodes.UpdateNetlossLimitSuccess,
				},
				[BETTING_LIMIT_ACTION_TYPE.removeBetCountLimitSuccess]: {
					context: BETTING_LIMIT_MESSAGE_CONTEXT.deleteBetCountLimit,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BettingLimitMessageCodes.DeleteBetCountLimitSuccess,
				},
				[BETTING_LIMIT_ACTION_TYPE.removeWageringLimitSuccess]: {
					context: BETTING_LIMIT_MESSAGE_CONTEXT.deleteWageringLimit,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BettingLimitMessageCodes.DeleteWageringLimitSuccess,
				},
				[BETTING_LIMIT_ACTION_TYPE.removeTimedWageringLimitSuccess]: {
					context: BETTING_LIMIT_MESSAGE_CONTEXT.deleteTimedWageringLimit,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BettingLimitMessageCodes.DeleteTimedWageringLimitSuccess,
				},
				[BETTING_LIMIT_ACTION_TYPE.removeNetlossLimitSuccess]: {
					context: BETTING_LIMIT_MESSAGE_CONTEXT.deleteNetlossLimit,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BettingLimitMessageCodes.DeleteNetlossLimitSuccess,
				},
				[BETTING_LIMIT_ACTION_TYPE.removeTimedWageringLimitWithPayloadSuccess]: {
					context: BETTING_LIMIT_MESSAGE_CONTEXT.deleteTimedWageringLimit,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BettingLimitMessageCodes.DeleteTimedWageringLimitWithPayloadSuccess,
				},
				[BETTING_LIMIT_ACTION_TYPE.removeNetlossLimitWithPayloadSuccess]: {
					context: BETTING_LIMIT_MESSAGE_CONTEXT.deleteNetlossLimit,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BettingLimitMessageCodes.DeleteNetlossLimitWithPayloadSuccess,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private service: BettingLimitService,
		private messageService: MessageService,
		private snackBarCacheService: SnackBarCacheService,
		private bettingLimitConfigService: BettingLimitConfigService,
		private netlossLimitPromptService: NetlossLimitPromptService
	) {}
}
